import { useMemo } from 'react';
import { createAuthenticatedApiClient } from '../api/apiClient';
import { rpcCall } from '../api/rpcClient';
import { resolveTriggerReconcilerRpc } from '../utils/reconcilerTrigger';
import { useAuth } from '../../features/auth/context/AuthContext';
import { domainLabel } from '../labels';
import type {
  ReconcilerExecutionDetail,
  ReconcilerHealth,
  ReconcilerResource,
  CustomerControlPlane,
} from '../../types/backend';
import type {
  AsyncDomainSummary,
  AsyncResourceState,
  AsyncExecution,
  ReconcilerStatus,
  ReconcilerExecution,
  ReconcilerPlan,
  ReconcilerApply,
  ReconcilerVerify,
  ReconcilerPlumbingStep,
  ReconcilerExecutionStatus,
  ReconcilerVerifyOutcome,
} from '../../types/frontend';

const VERIFY_SUMMARY: Record<ReconcilerVerifyOutcome, string> = {
  CONVERGED: 'Verified in AWS',
  SETTLING: 'Settling in AWS',
  DRIFTED: 'Drift detected',
};

function mapExecutionStatus(sfnStatus: string): ReconcilerExecutionStatus {
  switch (sfnStatus) {
    case 'SUCCEEDED': return 'CONVERGED';
    case 'FAILED': return 'FAILED';
    case 'TIMED_OUT': return 'FAILED';
    case 'ABORTED': return 'FAILED';
    case 'RUNNING': return 'IN_PROGRESS';
    case 'PENDING_REDRIVE': return 'PENDING';
    default: return 'PENDING';
  }
}

/**
 * A row built from the status record alone, for a run whose detail cannot be
 * shown. `error` and `cause` say why, so the row is never silently dropped.
 */
export function executionFromResource(
  resource: ReconcilerResource,
  error: string,
  cause: string | null,
): ReconcilerExecution {
  return {
    id: resource.execution_id ?? resource.resource_id,
    resource: resource.resource_id,
    resource_label: resource.resource_id,
    reconciler: resource.domain_type,
    reconciler_description: '',
    domain: resource.domain_type,
    domain_label: domainLabel(resource.domain_type),
    status: resource.status as ReconcilerExecutionStatus,
    started_at: resource.updated_at,
    total_ms: 0,
    plan: null,
    execute: null,
    verify: null,
    plumbing: [],
    phaseOutcomes: {},
    error,
    cause,
  };
}

export const HISTORY_UNAVAILABLE_MESSAGE = 'Run history no longer available';

export function mapToReconcilerExecution(
  detail: ReconcilerExecutionDetail,
  resource: ReconcilerResource,
): ReconcilerExecution {
  if (!detail.history_available) {
    return {
      ...executionFromResource(
        resource,
        HISTORY_UNAVAILABLE_MESSAGE,
        detail.history_unavailable_reason ?? null,
      ),
      historyUnavailable: true,
    };
  }
  const startedAt = detail.started_at;
  if (!startedAt) {
    throw new Error(`Execution ${detail.execution_arn} has history but no start time`);
  }
  const phases = detail.phases;
  const timing = detail.timing;

  let plan: ReconcilerPlan | null = null;
  if (phases?.plan) {
    const groups = (phases.plan.groups ?? []).map((g) => ({
      actions: g.actions.map((a) => ({
        display_name: a.display_name ?? a.action_type,
        description: a.description ?? '',
        action_type: a.action_type,
        resource_id: a.resource_id,
        status: 'pending' as const,
      })),
    }));
    plan = {
      duration_ms: timing?.plan_ms ?? 0,
      no_change: phases.plan.no_change ?? false,
      summary: phases.plan.summary ?? phases.plan.reason ?? '',
      reason: phases.plan.reason ?? null,
      groups,
    };
  }

  let execute: ReconcilerApply | null = null;
  if (phases?.apply) {
    const groups = [{
      actions: phases.apply.map((ar) => ({
        display_name: ar.display_name ?? ar.action_type,
        description: '',
        action_type: ar.action_type,
        resource_id: ar.resource_id,
        status: (ar.success ? 'done' : 'failed') as 'done' | 'failed',
        error: ar.error,
        error_detail: ar.error_detail,
      })),
    }];
    execute = {
      duration_ms: timing?.apply_ms ?? 0,
      groups,
    };
  }

  let verify: ReconcilerVerify | null = null;
  if (phases?.verify) {
    verify = {
      duration_ms: timing?.verify_ms ?? 0,
      outcome: phases.verify.outcome,
      summary: phases.verify.summary ?? VERIFY_SUMMARY[phases.verify.outcome],
      observations: phases.verify.observations ?? [],
    };
  }

  const plumbing: ReconcilerPlumbingStep[] = (timing?.plumbing ?? []).map((step) => ({
    display_name: step.display_name,
    duration_ms: step.duration_ms,
    status: 'done' as const,
  }));

  return {
    id: detail.execution_arn,
    resource: detail.resource_id || resource.resource_id,
    resource_label: detail.resource_label || detail.resource_id || resource.resource_id,
    reconciler: detail.reconciler_name || resource.domain_type,
    reconciler_description: detail.reconciler_description || '',
    domain: detail.reconciler_name || resource.domain_type,
    domain_label: domainLabel(detail.reconciler_name || resource.domain_type),
    status: (resource.status as ReconcilerExecutionStatus) ?? mapExecutionStatus(detail.status),
    started_at: startedAt,
    total_ms: detail.total_ms ?? 0,
    plan,
    execute,
    verify,
    plumbing,
    phaseOutcomes: (phases?.outcomes ?? {}) as ReconcilerExecution['phaseOutcomes'],
    error: detail.error ?? null,
    cause: detail.cause ?? null,
  };
}

export const createOpsService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    listReconcilers: () =>
      rpcCall<AsyncDomainSummary[]>(client, 'ops.ListReconcilers'),

    // ops.ListReconcilerResources is paginated ({resources, next_token}) to bound
    // the payload; unwrap to the resource array the kernel view consumes (first
    // page, <=50).
    listReconcilerResources: async (
      domain_type: string,
      status?: string,
    ): Promise<ReconcilerResource[]> => {
      const page = await rpcCall(client, 'ops.ListReconcilerResources', {
        domain_type,
        ...(status ? { status } : {}),
      });
      return page.resources ?? [];
    },

    getState: (domain_type: string, resource_id: string) =>
      rpcCall<AsyncResourceState>(client, 'ops.GetState', {
        domain_type,
        resource_id,
      }),

    getResult: (domain_type: string, resource_id: string) =>
      rpcCall<{ ready: boolean; data: unknown; error: string | null }>(
        client,
        'ops.GetResult',
        { domain_type, resource_id }
      ),

    listExecutions: (state_machine_arn: string, resource_id?: string) =>
      rpcCall<AsyncExecution[]>(client, 'ops.ListExecutions', {
        state_machine_arn,
        ...(resource_id ? { resource_id } : {}),
      }),

    getExecutionDetail: (execution_arn: string) =>
      rpcCall(client, 'ops.GetExecutionDetail', {
        execution_arn,
      }),

    start: (
      domain_type: string,
      resource_id: string,
      desired_data: Record<string, unknown>,
      state_machine_arn: string
    ) =>
      rpcCall<{ execution_arn: string }>(client, 'ops.Start', {
        domain_type,
        resource_id,
        desired_data,
        state_machine_arn,
      }),

    pauseReconciler: (domain_type: string) =>
      rpcCall<{ domain_type: string; enabled: boolean }>(
        client,
        'ops.PauseReconciler',
        { domain_type }
      ),

    resumeReconciler: (domain_type: string) =>
      rpcCall<{ domain_type: string; enabled: boolean }>(
        client,
        'ops.ResumeReconciler',
        { domain_type }
      ),

    getReconcilerStatus: (domain_type: string) =>
      rpcCall<ReconcilerStatus>(client, 'ops.GetReconcilerStatus', {
        domain_type,
      }),

    getControlPlaneHealth: () =>
      rpcCall<ReconcilerHealth[]>(client, 'ops.GetControlPlaneHealth'),

    getReconcilerHealth: (domain_type: string) =>
      rpcCall(client, 'ops.GetReconcilerHealth', {
        domain_type,
      }),

    getCustomerControlPlane: (customer_id: string) =>
      rpcCall<CustomerControlPlane>(client, 'ops.GetCustomerControlPlane', {
        customer_id,
      }),

    triggerReconciler: (domain_type: string) => {
      const rpcMethod = resolveTriggerReconcilerRpc(domain_type);
      if (!rpcMethod) {
        return Promise.reject(new Error(`No trigger RPC for reconciler: ${domain_type}`));
      }
      return rpcCall<{ dispatched: number; errors: number }>(
        client,
        rpcMethod,
        { domain_type }
      );
    },
  };
};

export const useOpsService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    return createOpsService(idToken);
  }, [idToken]);
};
